import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import type { ActivityLogEventType } from './useGetActivityLogs';
import { AutomationTarget } from './useGetOrderWorkflowStatus';
import { ManualFlagType } from './useTogglePartFlag';
import { PartType } from './useGetSettings';

/** System flag types set by the backend (not user manual-flag quick-select). */
export type OrderPartFlagType = 'in-stock';

export type ReKeyStatus = 'not-started' | 'flagged' | 'completed' | 'skipped' | 'in-review' | 'failed' | 'advisor' | 'needed';
export interface ReKeyPart {
    _id: string;
    title: string;
    status: ReKeyStatus;
    lineNumber: number;
    /** @deprecated Prefer originalQty; retained for older payloads. */
    qty: number;
    /** Original ordered quantity (Estimate column). Falls back to `qty` when absent. */
    originalQty?: number;
    /** Updated / remaining quantity (Part Changes). Null/absent → show ---. */
    modifiedQty?: number | null;
    flagReason?: string;
    originalPartType: string;
    originalPartNumber: string;
    originalPrice: number;
    modifiedPartType: string | null;
    modifiedPartNumber: string | null;
    modifiedPrice: number | null;
    lineNotes?: string;
    advisor?: {
        score: number;
        description: string;
        actualPrice: number | null;
        allowablePrice: number | null;
        variance: number | null;
    };
}

export interface AdvisorReportLine {
    lineNumber: number;
    orderPartId: string;
    title: string;
    score: number;
    description: string;
    actualPrice: number | null;
    allowablePrice: number | null;
    variance: number | null;
    partNumber: string;
}

export interface AdvisorReport {
    orderId: string;
    timestamp: string; // Date comes as string from JSON
    score: number;
    lines: Array<AdvisorReportLine>;
}

export type PartStatus =
    'pre-quoting' | // still in partbay
    'quoting' | // asking for quotes
    'quoting-waiting' | // asked for quotes
    'no-quoted' | // no quoted
    'quoting-deleted' | // deleted from quoting
    'selection' | // quotes are back, parsing
    'selection-excluded' | // excluded from selection
    'selection-excluded-temporary' | // excluded from selection because its flagged then re-added
    'ordered' | // orders have been submitted
    'confirmed'; // order has been confirmed by supplier

export interface QuotePart {
    _id: string;
    name: string;
    partNumber: string;
    quantity: number;
    type: string;
    quotedListPrice: number;
    excludeQuoting: boolean;
    estimatePartType: PartType;
    lineNumber: number;
    estimateListPrice: number;
    status: PartStatus;
    flag?: boolean;
    flagReason?: string;
    flagType?: OrderPartFlagType;
    manualFlagType?: ManualFlagType;
    isDuplicate: boolean;
    quoteAndExclude?: boolean;
    assemblyColor?: string;
    assemblySubParts?: Array<string>;
}

export type SelectionStatus = 'partbay' | 'manual' | 'flagged' | 'excluded' | 'ordered';
export type SelectionChangeIndicator = 'rejected-cancelled' | 'returned';

export interface SelectionPart {
    partId: string;
    name: string;
    partNumber: string;
    correctedPartNumber?: string;
    selectedOptionId?: string;
    type?: string;
    vendor?: string;
    delivery?: string;
    deliveryHours?: number;
    quantity?: number;
    listPrice?: number;
    buyPrice?: number;
    grossMargin?: number;
    notes?: string;
    estimatingNotes?: string;
    flag?: boolean;
    flagReason?: string;
    flagType?: OrderPartFlagType;
    manualFlagType?: string;
    excludeOrdering?: boolean;
    listPriceAdjusted?: boolean;
    /** Quoted list before a price-match adjustment. Present when the API sends it. */
    originalListPrice?: number;
    isGlass?: boolean;
    status: PartStatus;
    manuallyAdjusted: boolean;
    isAssembly: boolean;
    selectionStatus: SelectionStatus;
    canMarkUnordered: boolean;
    changeIndicators?: Array<SelectionChangeIndicator>;
}

export interface QuotingTab {
    key: string;
    title: string;
    parts: Array<QuotePart>;
    scheduled?: Date;
    delivered?: Array<{ markteplace: AutomationTarget | 'ccc-sp'; supplier?: string; status: 'fetched' | 'pending' | 'failed'; }>;
    scheduledArrivalDate?: Date;
    timeQuoted?: Date;
    timesExtended?: number;
    quotedMinutes?: number;
    quotedBy?: string;
    estReturnTime?: Date | null;
    preOrder?: boolean;
    allowAutoOrder?: boolean;
    expedited?: boolean;
    oemEndoresement?: boolean;
    total: number;
    status?: 'open' | 'closed' | 'pending';
    /** EMS submitted with this quote request. `null` hides the Quoting-tab EMS icon. */
    emsId: string | null;
}

/** Shared top-level `ems` on POST /order/detail and GET /order/drawer (ENG-1910). */
export interface OrderEmsPush {
    _id: string;
    /** ISO-8601 timestamp string. */
    timestamp: string;
    /** `true` if this push was sent to marketplaces (EMS `processed`); mirrors the EMS detail modal status. */
    wasSubmitted: boolean;
    /** `true` if this is the most recent push for the order. */
    isLatest: boolean;
}

export interface OrderEmsSummary {
    /** EMS backing the current New Parts list. `null` if the order has no EMS. */
    currentEmsId: string | null;
    /**
     * `true` when every New Parts orderPart.emsListPrice matches that part's
     * listPrice in the most recent EMS push (joined by lineNumber).
     */
    matchesLatestEmsPrices: boolean;
    needsQuotes: boolean;
    total: number;
    pushes: Array<OrderEmsPush>;
}

export type SelectionTabKey = 'all' | 'pending' | 'excluded' | 'no-quoted' | 'ordered';
export interface SelectionTab {
    key: SelectionTabKey;
    title: string;
    total: number;
    parts: Array<SelectionPart>;
}

export interface OrderedPart {
    partId: string;
    name: string;
    partNumber: string;
    supplierPartNumber?: string | null;
    correctedPartNumber?: string;
    type: string;
    vendor: string;
    delivery: string;
    deliveryHours: number;
    correctedDelivery?: string;
    correctedDeliveryHours?: number;
    /** Ordered / supplier-order total qty (estimate remaining + inventoried). */
    quantity: number;
    /** Units already moved to inventory on this line. */
    quantityInventoried?: number;
    listPrice: number;
    buyPrice: number;
    correctedBuyPrice?: number;
    correctedListPrice?: number;
    grossMargin: number;
    correctedGrossMargin?: number;
    isAssembly: boolean;
    cancelled?: boolean;
    returned?: boolean;
    canReturn: boolean;
    /** True when the part has been marked received (via the Delivery Board or Carl). */
    received?: boolean;
    receivedAt?: string | null;
    /** Display name of the user who marked the part received. */
    receivedByName?: string | null;
}

export type SupplierOrderStatus = 'rejected' | 'confirmed' | 'ordered' | 'cancelled';
export interface OrderedSection {
    key: string;
    title: string;
    phoneNumber: string | null;
    email: string | null;
    hasChanges: boolean;
    parts: Array<OrderedPart>;
    grossMargin: number;
    totalBuyPrice: number;
    timePlaced: string;
    totalListPrice: number;
    displayId: string | null;
    cccPoCreationStatus?: 'failed' | 'completed';
    cccPoCreationFailedReason?: string;
    status: SupplierOrderStatus;
    source?: AutomationTarget | 'inventory';
    /**
     * The manual Mark Confirmed action applies: a PO still at Ordered on any
     * marketplace, with at least one part not cancelled or returned.
     */
    canManualConfirm?: boolean;
    awaitingPartstraderConfirmation?: boolean;
    /** Present when `source === 'inventory'`. */
    purchaseOrderId?: string | null;
    /** Set when the inventory PO completion PDF was generated; otherwise null. */
    invoiceNumber?: string | null;
    hasSupplierThread?: boolean;
    /** Rollup: every in-flight part on this supplier order has been marked received. */
    received?: boolean;
    receivedAt?: string | null;
}

export interface OrderedTab {
    key: 'all' | 'ordered' | 'confirmed' | 'rejected' | 'cancelled' | 'returned';
    title: string;
    total: number;
    sections: Array<OrderedSection>;
}

export interface OrderNote {
    _id: string;
    userId: string;
    user: string;
    timestamp: string;
    note: string;
    type: 'note' | 'message';
    mentionedUserIds?: string[];
}

export interface RekeySection {
    partsConfirmed: number;
    partsReKeyed: number;
    partsFlagged: number;
    partsAdvisor: number;
    status: ReKeyStatus;
    parts: Array<ReKeyPart>;
    rekeyError?: string;
}

export interface SupplierNote {
    _id: string;
    supplierName: string;
    question: string;
    timeReceived: string;
}

export interface ActivityLogEntry {
    _id: string;
    orderId: string;
    userId: string;
    userName: { firstName: string; lastName: string; } | null;
    timestamp: string;
    eventType: ActivityLogEventType;
    /** Non-human origin of the entry (e.g. 'carl-agent'). Absent/null for direct human actions. */
    source?: 'carl-agent' | null;
    /** Enriched display fields. Set for workflow-cancelled (Workflow, Source). */
    metadata?: Record<string, string>;
}

export interface ActivityLogResponse {
    logs: ActivityLogEntry[];
    totalCount: number;
}

export interface ReviewedByUser {
    _id: string;
    firstName: string;
    lastName: string;
    displayName: string;
}

export interface OrderDetailResponse {
    canEnterEstimate: boolean;
    vehicleName: string;
    /** Vehicle make from the order; empty/omitted when unknown. */
    vehicleMake?: string;
    vin: string;
    mileage?: number;
    claimNumber?: string;
    roNumber: string;
    locationName: string;
    locationId: string;
    dealerReferral?: string;
    timeCreated: Date;
    lastUpdated?: Date;
    customer: string;
    insurance: string;
    estimator: string;
    drpJob: boolean;
    archived: boolean;
    canComplete: boolean;
    completed: boolean;
    needsSourcePartsMessage: string | null;
    oemEndorsement: boolean;
    oemCertificationPriceDifference?: number;
    oemCertification: boolean;
    quotesOnly: boolean;
    potentialTotal: boolean;
    teslaOrderError: boolean;
    quoteRequestErrors: Array<{ marketplace: AutomationTarget; error: string; }>;
    preOrderBackorder?: boolean;
    quoteRequestAllowedAutoOrder?: boolean;
    reviewedByUsers: Array<ReviewedByUser>;
    daysActive: string;
    locationEditable: boolean;
    totalParts: number;
    manuallyFlagged: boolean;
    partsOrdered: number;
    scheduledOrderPlacement: Date | null;
    orderValue: number;
    grossMargin: number;
    minGrossMarginThreshold?: number | null;
    deliveryTime: string | null;
    isEvOrder: boolean;
    deliveryETA: string | null;
    canAttemptPnChange: boolean;
    notes: Array<OrderNote>;
    /** Display names eligible for @mention on this order — used to highlight mentions without a separate fetch. */
    mentionNames?: string[];
    newParts: {
        parts: Array<QuotePart>;
    };
    /** Shared EMS summary — same shape as GET /order/drawer `ems`. */
    ems: OrderEmsSummary;
    quoting: Array<QuotingTab>;
    selection: Array<SelectionTab>;
    ordered: Array<OrderedTab>;
    rekey?: RekeySection;
    supplierNotes: Array<SupplierNote>;
    supplierMessageCounts: Record<string, number>;
    activityLog: ActivityLogResponse;
}

interface OrderInput {
    orderId: string;
}

const getOrderDetail = async (
    sessionId: string,
    orderId: string,
): Promise<OrderDetailResponse> => {
    const { data } = await axios.post(`${API_URL}/order/detail`,
        {
            orderId,
        } as OrderInput,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export const useGetOrderDetail = (
    orderId: string,
    options?: {
        enabled?: boolean;
    }
) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: ['/order/detail', orderId],
        queryFn: () => getOrderDetail(sessionContext?.sessionId ?? '', orderId),
        enabled: !!sessionContext?.sessionId && !!orderId && (options?.enabled !== false),
        staleTime: 20 * 1000,
        refetchInterval: 20 * 1000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });
};
